<template>
  <div class="embassies-container" ref="embassiesContainerRef">
    <!-- Main Loading State -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading embassies...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-message">
      <i class="fas fa-exclamation-triangle error-icon"></i>
      <div>
        <h3 class="error-title">Error Loading Embassies</h3>
        <p class="error-details">{{ error }}</p>
        <button @click="fetchEmbassies" class="retry-button">
          <i class="fas fa-sync-alt"></i> Retry
        </button>
      </div>
    </div>

    <!-- Content when loaded -->
    <template v-else-if="isStoreReady">
      <!-- Header Section -->
    <div class="header-section">
      <div class="header-background"></div>
      <div class="header-content">
       <h1 class="page-title">{{ pageTitle }}</h1>
        <p class="page-subtitle">{{$t('embassy.subtitle')}}</p>
      </div>
    </div>

      <!-- Controls Section -->
      <div class="controls-section">
        <!-- Search Bar -->
        <div class="search-bar">
          <div class="search-input-container">
            <i class="fas fa-search search-icon"></i>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('searchEmbassies')"
              class="search-input"
            />
            <button v-if="searchQuery" @click="searchQuery = ''" class="clear-button">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Submenu Tabs -->
        <div class="submenu-tabs">
          <button
            v-for="submenu in submenus"
            :key="submenu.id"
            @click="activeSubmenu = submenu.id"
            class="submenu-tab"
            :class="{ 'active-tab': activeSubmenu === submenu.id }"
          >
            {{ submenu.text }}
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!paginatedEmbassies.length" class="empty-state">
        <div class="empty-content">
          <i class="fas fa-globe-americas empty-icon"></i>
          <h3 class="empty-title">{{ $t('embassy.noEmbassiesFoundTitle') }}</h3>
          <p class="empty-message">{{$t('embassy.noEmbassiesFoundMessage')}}</p>
          <button @click="searchQuery = ''" class="reset-button">
            {{$t('embassy.searchagain')}}
          </button>
        </div>
      </div>

      <!-- Embassies Grid -->
      <div v-else class="embassies-grid">
        <div
          v-for="embassy in paginatedEmbassies"
          :key="embassy.id"
          @click="viewEmbassyDetail(embassy)"
          class="embassy-card"
        >
          <div class="flag-container" :class="{'has-flag': embassy.displayImage}">
            <img
              v-if="embassy.displayImage"
              :src="embassy.displayImage"
              :alt="`Flag of ${embassy.country}`"
              class="embassy-flag"
              @error="handleImageError"
            />
          </div>
          <div class="embassy-info">
            <h3 class="embassy-country">{{ embassy.country }}</h3>
            <p class="embassy-name">{{ embassy.name }}</p>
            <div class="view-details">
              {{$t('embassy.viewdetails')}} <i class="fas fa-chevron-right"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination-controls">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="pagination-button prev-next"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        
        <button
          v-for="page in visiblePages"
          :key="page"
          @click="goToPage(page)"
          :class="{ 
            'active-page': currentPage === page,
            'ellipsis': typeof page !== 'number'
          }"
          class="pagination-button"
          :disabled="typeof page !== 'number'"
        >
          {{ page }}
        </button>
        
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="pagination-button prev-next"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useEmbassyStore } from '@/stores/embassy';
import { useI18n } from 'vue-i18n';
import countryCodes from '@/utils/countryCodes';

export default {
  name: 'EmbassiesList',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const embassyStore = useEmbassyStore();
    const { t, locale } = useI18n();
    const pageTitle = computed(() => {
      return activeSubmenu.value === 'inCameroon' 
        ? t('embassy.foreignEmbassiesInCameroon') 
        : t('embassy.cameroonEmbassiesAbroad');
    });
    // Refs
    const searchQuery = ref('');
    const activeSubmenu = ref('inCameroon');
    const currentPage = ref(1);
    const itemsPerPage = 12;
    const embassiesContainerRef = ref(null);
    const isInitialized = ref(false);

    // Computed Properties
    const submenus = computed(() => [
        { id: 'inCameroon', text: t('embassy.foreignEmbassiesInCameroon') },
      { id: 'abroad', text: t('embassy.cameroonEmbassiesAbroad') }
    
    ]);

    const isLoading = computed(() => embassyStore.isLoading);
    const error = computed(() => embassyStore.error);
    const isStoreReady = computed(() => !embassyStore.isLoading && embassyStore.embassies.length > 0);

    const filteredEmbassies = computed(() => {
      if (!isStoreReady.value) return [];
      
      return embassyStore.embassies.filter(embassy => {
        const matchesSearch = searchQuery.value === '' || 
          embassy.country?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
          embassy.name?.toLowerCase().includes(searchQuery.value.toLowerCase());
        
        const matchesSubmenu = activeSubmenu.value === 'inCameroon' 
          ? embassy.typeembassy === 'cameroon'
          : embassy.typeembassy === 'foreign'; 
        
        return matchesSearch && matchesSubmenu;
      });
    });

    const totalPages = computed(() => Math.ceil(filteredEmbassies.value.length / itemsPerPage));

    const paginatedEmbassies = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      
      return filteredEmbassies.value.slice(start, end).map(embassy => {
        let displayImage = null;
        
        // Use country flag if available
        const countryName = embassy.country?.trim();
        const countryCode = countryCodes[countryName];
        if (countryCode) {
          // External CDN for flag images — whitelisted in CSP img-src
          displayImage = `https://flagcdn.com/w160/${countryCode.toLowerCase()}.png`;
        }
        
        return { ...embassy, displayImage: displayImage || '' };
      });
    });

    const visiblePages = computed(() => {
      const pages = [];
      const total = totalPages.value;
      const current = currentPage.value;
      
      // Always show first page
      pages.push(1);
      
      // Show current page and nearby pages
      if (current > 3) pages.push('...');
      if (current === total && total > 3) pages.push(current - 2);
      if (current > 2) pages.push(current - 1);
      if (current !== 1 && current !== total) pages.push(current);
      if (current < total - 1) pages.push(current + 1);
      if (current === 1 && total > 3) pages.push(current + 2);
      if (current < total - 2) pages.push('...');
      
      // Always show last page if different from first
      if (total > 1) pages.push(total);
      
      return pages;
    });

    // Methods
    const fetchEmbassies = () => embassyStore.fetchEmbassies(locale.value);

    const viewEmbassyDetail = (embassy) => {
      router.push({ 
        name: 'embassy-detail', 
        params: { name: embassy.country }
      });
    };

    const handleImageError = (event) => {
      event.target.style.display = 'none';
      event.target.parentElement.classList.remove('has-flag');
    };

    const goToPage = (page) => {
      if (typeof page === 'number' && page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        embassiesContainerRef.value?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Watchers
    watch([searchQuery, activeSubmenu], () => {
      if (!isInitialized.value) return;
      currentPage.value = 1;
    });

    watch([searchQuery, activeSubmenu, currentPage], () => {
      if (!isInitialized.value) return;
      const newQuery = { ...route.query };
      if (activeSubmenu.value !== 'inCameroon') newQuery.tab = activeSubmenu.value;
      else delete newQuery.tab;
      if (searchQuery.value) newQuery.q = searchQuery.value;
      else delete newQuery.q;
      if (currentPage.value > 1) newQuery.page = currentPage.value;
      else delete newQuery.page;
      router.replace({ query: newQuery });
    }, { deep: true });

    // Lifecycle Hooks
    onMounted(async () => {
      activeSubmenu.value = route.query.tab || 'inCameroon';
      searchQuery.value = route.query.q || '';
      currentPage.value = parseInt(route.query.page, 10) || 1;
      await fetchEmbassies();
      isInitialized.value = true;
    });

    // Re-fetch embassies when language changes
    watch(() => locale.value, async (newLang, oldLang) => {
      if (newLang === oldLang) return;
      // Clear stale embassy data so fresh translations are fetched
      embassyStore.$patch({ embassies: [], lastFetchedLang: null });
      await fetchEmbassies();
    });
    
    return {
      searchQuery,
      activeSubmenu,
      currentPage,
      submenus,
      isLoading,
      error,
      isStoreReady,
      paginatedEmbassies,
      totalPages,
      visiblePages,
      viewEmbassyDetail,
      goToPage,
      fetchEmbassies,
      handleImageError,
      embassiesContainerRef,
      pageTitle
    };
  }
};
</script>

<style scoped>
.embassies-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #2d3748;
}

/* Header Section */
.header-section {
  position: relative;
  text-align: center;
  margin: -80px -20px 20px -20px; /* Negative margins to pull it up and out */
  padding: 25px 20px; /* Reduced padding */
  border-radius: 0 0 1px 1px; /* Only round bottom corners */
  overflow: hidden;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* Added subtle shadow */
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #144A78 0%, #2a4365 100%); /* Darker gradient */
  z-index: -1;
}

.header-background::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.07' fill-rule='evenodd'/%3E%3C/svg%3E");
  opacity: 0.5;
}

.header-content {
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
}

.page-title {
  font-size: 1.7rem; /* Slightly smaller */
  font-weight: 700;
  color: white;
  margin-bottom: 6px; /* Reduced */
  line-height: 1.2;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.page-subtitle {
  font-size: 1rem; /* Slightly smaller */
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .header-section {
    margin: -15px -15px 15px -15px;
    padding: 20px 15px;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .page-subtitle {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .header-section {
    margin: -10px -10px 10px -10px;
    padding: 18px 10px;
    border-radius: 0 0 12px 12px;
  }
  
  .page-title {
    font-size: 1.3rem;
    margin-bottom: 4px;
  }
}

/* Controls Section */
.controls-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 40px;
}

/* Search Bar */
.search-bar {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.search-input-container:focus-within {
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.2);
  border-color: #90cdf4;
}

.search-icon {
  position: absolute;
  left: 16px;
  color: #a0aec0;
  font-size: 1rem;
}

.search-input {
  flex-grow: 1;
  padding: 14px 20px 14px 48px;
  border: none;
  font-size: 1rem;
  outline: none;
  color: #2d3748;
  background: transparent;
  border-radius: 12px;
}

.search-input::placeholder {
  color: #a0aec0;
}

.clear-button {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 8px;
  font-size: 0.9rem;
  transition: color 0.2s;
}

.clear-button:hover {
  color: #718096;
}

/* Submenu Tabs */
.submenu-tabs {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.submenu-tab {
  padding: 12px 24px;
  background-color: #edf2f7;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.95rem;
  color: #4a5568;
}

.submenu-tab:hover {
  background-color: #e2e8f0;
  color: #2d3748;
}

.active-tab {
  background-color: #4299e1;
  color: white;
  box-shadow: 0 2px 8px rgba(66, 153, 225, 0.3);
}

.active-tab:hover {
  background-color: #3182ce;
  color: white;
}

/* Loading Styles */
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  min-height: 400px;
}

.loading-spinner {
  border: 4px solid rgba(66, 153, 225, 0.2);
  border-radius: 50%;
  border-top: 4px solid #4299e1;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 1.1rem;
  color: #4a5568;
  font-weight: 500;
}

/* Error Styles */
.error-message {
  display: flex;
  align-items: center;
  background-color: #fff5f5;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 40px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #f56565;
  max-width: 800px;
  margin: 0 auto;
}

.error-icon {
  color: #f56565;
  font-size: 2rem;
  margin-right: 20px;
}

.error-title {
  color: #c53030;
  margin-bottom: 8px;
  font-size: 1.25rem;
  font-weight: 600;
}

.error-details {
  color: #718096;
  margin-bottom: 16px;
  line-height: 1.5;
}

.retry-button {
  background-color: #f56565;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background-color 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.retry-button:hover {
  background-color: #e53e3e;
}

.retry-button i {
  font-size: 0.9rem;
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  background-color: #f7fafc;
  border-radius: 12px;
  margin: 40px 0;
  border: 1px dashed #cbd5e0;
}

.empty-content {
  text-align: center;
  max-width: 400px;
}

.empty-icon {
  font-size: 3rem;
  color: #a0aec0;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 12px;
}

.empty-message {
  color: #718096;
  margin-bottom: 20px;
  line-height: 1.5;
}

.reset-button {
  background-color: #4299e1;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background-color 0.2s ease;
}

.reset-button:hover {
  background-color: #3182ce;
}

/* Embassies Grid */
.embassies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 48px;
}

.embassy-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  height: 240px; /* Reduced card height */
}

.flag-container {
  height: 140px; /* Reduced flag container height */
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #ebf8ff 0%, #bee3f8 100%);
}

.flag-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 5.588a.5.5 0 01.223.67l-7.14 14.28a.5.5 0 00.223.67l4.46 2.23a.5.5 0 01.223.67l-4.46 8.92a.5.5 0 00.224.67l4.46 2.23a.5.5 0 01.223.67l-7.14 14.28a.5.5 0 01-.894 0l-7.14-14.28a.5.5 0 01.223-.67l4.46-2.23a.5.5 0 00.223-.67l-4.46-8.92a.5.5 0 01.223-.67l4.46-2.23a.5.5 0 00.223-.67l-7.14-14.28a.5.5 0 01.894 0l7.14 14.28a.5.5 0 00.894 0l7.14-14.28a.5.5 0 01.67-.223z' fill='%2390cdf4' fill-opacity='0.2' fill-rule='evenodd'/%3E%3C/svg%3E");
}

.embassy-flag {
  width: auto;
  height: 70px;
  object-fit: contain;
  z-index: 2;
  transition: transform 0.3s ease;
}

.embassy-card:hover .embassy-flag {
  transform: scale(1.1);
}

.embassy-info {
  padding: 16px;
  border-top: 1px solid #edf2f7;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.embassy-country {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
  line-height: 1.3;
  text-align: center;
}

.embassy-name {
  font-size: 0.85rem;
  color: #4a5568;
  margin-bottom: 12px;
  line-height: 1.4;
  text-align: center;
}

.view-details {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3182ce;
  font-size: 0.8rem;
  font-weight: 500;
  gap: 6px;
  transition: color 0.2s;
}

.embassy-card:hover .view-details {
  color: #3182ce;
}

.view-details i {
  font-size: 0.8rem;
  transition: transform 0.2s;
}

.embassy-card:hover .view-details i {
  transform: translateX(2px);
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
  flex-wrap: wrap;
}

.pagination-button {
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  color: #4a5568;
  font-weight: 500;
  padding: 0 12px;
}

.pagination-button:hover:not(:disabled) {
  background-color: #edf2f7;
  border-color: #cbd5e0;
  color: #2d3748;
}

.pagination-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  color: #a0aec0;
}

.pagination-button.active-page {
  background-color: #4299e1;
  color: white;
  border-color: #4299e1;
}

.pagination-button.active-page:hover {
  background-color: #3182ce;
  border-color: #3182ce;
}

.pagination-button.ellipsis {
  background: none;
  border: none;
  cursor: default;
  color: #718096;
}

.pagination-button.prev-next {
  padding: 0 16px;
}

/* Responsive Adjustments */
@media (max-width: 1024px) {
  .embassies-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
  }
}

@media (max-width: 768px) {
    .header-section {
    padding: 30px 20px;
    margin-bottom: 20px;
  }
  
  .page-title {
    font-size: 1.6rem;
  }
  
  .page-subtitle {
    font-size: 1rem;
  }
  
  .embassy-card {
    height: 220px;
  }
  
  .flag-container {
    height: 120px;
  }
  
  .embassy-flag {
    height: 60px;
  }
  .embassies-container {
    padding: 32px 20px;
  }
  
  .page-title {
    font-size: 1.8rem;
  }
  
  .controls-section {
    gap: 20px;
    margin-bottom: 32px;
  }
  
  .flag-container {
    height: 140px;
  }
}

@media (max-width: 480px) {
    .header-section {
    padding: 24px 16px;
    border-radius: 12px;
  }
  
  .page-title {
    font-size: 1.4rem;
  }
  
  .embassy-card {
    height: 200px;
  }
  
  .flag-container {
    height: 100px;
  }
  .embassies-container {
    padding: 24px 16px;
  }
  
  .page-title {
    font-size: 1.6rem;
  }
  
  .page-subtitle {
    font-size: 1rem;
  }
  
  .submenu-tabs {
    gap: 8px;
  }
  
  .submenu-tab {
    padding: 10px 16px;
    font-size: 0.9rem;
  }
  
  .embassies-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .pagination-button {
    min-width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }
}
</style>